"use client";

import { AttendanceStatus, EventJourneyStatus } from "@prisma/client";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { RiEyeLine, RiMapPinLine } from "react-icons/ri";
import useUserPemissions from "~/hooks/useUserPermissions";
import { useEventDetailsPageStore } from "~/state/store";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { cn } from "~/utils/fontsConfig";
import { getTimezoneFromCoordinates } from "~/utils/timezones";
import { If } from "../../Conditional";
import { Label } from "../../shadcn/Label";
import GoogleMapsAutoComplete, {
    GooglePlace,
    extractCityFromGooglePlace,
    extractCoordinates,
    useGooglePlace,
} from "../../themed/GoogleMapsAutoComplete";
import { LockedSwitch } from "../../themed/LockedSwitch";
import ValidVenueAlert from "../../themed/ValidVenueAlert";
import { EventFormData } from "./EventFormDataType";
import { Button } from "../../themed/Button";
import { MdOutlineClear } from "react-icons/md";
import VenueRecommender from "./VenueRecommender";
import { RouterOutputs } from "~/utils/api";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/shadcn/Tooltip";

const ProtectionTooltip = ({
    isProtectedVisible,
}: {
    isProtectedVisible: boolean;
}) => {
    return (
        <TooltipProvider delayDuration={0}>
            <Tooltip>
                <TooltipTrigger className="flex flex-row items-center justify-start gap-2">
                    <RiEyeLine
                        className={cn(
                            "text-gray-500",
                            isProtectedVisible && "text-secondary",
                        )}
                    />
                    Location is protected
                </TooltipTrigger>
                <TooltipContent sideOffset={-100} className="font-normal">
                    <p>Exact location is only visible to hosts.</p>
                    <p>
                        Precise location will be visible to regular attendees in
                        accordance with event settings
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

type Props = {
    event: EventBySlugOutput;
    isLoadingEvent: boolean;
    permissions?: RouterOutputs["permissions"]["getUserPermissions"];
};

const EventLocation = ({ event, isLoadingEvent, permissions }: Props) => {
    const form = useFormContext<EventFormData>();
    const { isAdmin, isManager } = useUserPemissions({
        event: event || null,
    });
    const { panelMode } = useEventDetailsPageStore();

    const [googlePlaceId, setGooglePlaceId] = useState<string | null>();
    const [googlePlace, setGooglePlace] = useGooglePlace(googlePlaceId);

    const [googlePlaceIdLocked, setGooglePlaceIdLocked] = useState<
        string | null
    >();
    const [googlePlaceLocked, setGooglePlaceLocked] =
        useGooglePlace(googlePlaceIdLocked);

    const [isLockedVisible, setIsLockedVisible] = useState(false);

    useEffect(() => {
        if (panelMode !== "edit") return;
        setGooglePlaceId(event.googleMapsPlaceId);
        // @ts-ignore - this just doesn't have the json types
        setGooglePlaceIdLocked(event.locationLocked?.googleMapsPlaceId);
    }, [panelMode]);

    const onUpdateGoogleMaps = async (
        value: GooglePlace,
        isLocked: boolean,
    ) => {
        const { longitude, latitude } = extractCoordinates(value?.geometry);

        //TODO: throw an error if the google api isn't working. We can't just save with a different timezone
        const timezone =
            (await getTimezoneFromCoordinates(latitude ?? 0, longitude ?? 0)) ??
            "Etc/UTC";

        // timezone based on google place's lat long
        const updates = {
            timezone: timezone,
            city: extractCityFromGooglePlace(value),
            longitude,
            latitude,
            googleMapsPlaceId: value?.place_id ?? null,
            googleMapsPlaceName: value?.name ?? null,
            googleMapsPlaceTypes: value?.types ?? null,
            location: value.formatted_address ?? "",
            locationName: value?.name ?? null,
            useGoogleMapsLocation: !!value?.place_id,
            country:
                value?.address_components?.find((c) =>
                    c.types.includes("country"),
                )?.long_name ?? null,
        };

        if (!isLocked) {
            // set values
            for (const [k, v] of Object.entries(updates)) {
                // @ts-ignore
                form.setValue(k, v, { shouldDirty: true });
            }
            setGooglePlace(value);
        } else {
            form.setValue("locationLocked", updates, { shouldDirty: true });
            setGooglePlaceLocked(value);
        }
    };

    const handleRemoveLocation = () => {
        form.setValue("googleMapsPlaceId", null, { shouldDirty: true });
        form.setValue("googleMapsPlaceName", null, { shouldDirty: true });
        form.setValue("googleMapsPlaceTypes", undefined, { shouldDirty: true });
        form.setValue("location", "", { shouldDirty: true });
        form.setValue("locationName", null, { shouldDirty: true });
        form.setValue("useGoogleMapsLocation", false, { shouldDirty: true });
        form.setValue("latitude", null, { shouldDirty: true });
        form.setValue("longitude", null, { shouldDirty: true });
        form.setValue("city", null, { shouldDirty: true });
        form.setValue("country", null, { shouldDirty: true });
        setGooglePlaceId(null);
        setGooglePlace(undefined);
    };

    const handleUseRecommendedLocation = () => {
        const recommendedVenuePlaceId = form.getValues(
            "recommendedVenuePlaceId",
        );

        if (recommendedVenuePlaceId) {
            const service = new google.maps.places.PlacesService(
                document.createElement("div"),
            );

            service.getDetails(
                {
                    placeId: recommendedVenuePlaceId,
                },
                async (place, status) => {
                    if (
                        status === google.maps.places.PlacesServiceStatus.OK &&
                        place
                    ) {
                        const { longitude, latitude } = extractCoordinates(
                            place?.geometry,
                        );
                        const timezone =
                            (await getTimezoneFromCoordinates(
                                latitude ?? 0,
                                longitude ?? 0,
                            )) ?? "Etc/UTC";

                        const updates = {
                            timezone: timezone,
                            city: extractCityFromGooglePlace(place),
                            longitude,
                            latitude,
                            googleMapsPlaceId: place?.place_id ?? null,
                            googleMapsPlaceName: place?.name ?? null,
                            googleMapsPlaceTypes: place?.types ?? null,
                            location: place?.formatted_address ?? "",
                            locationName: place?.name ?? null,
                            useGoogleMapsLocation: !!place.place_id,
                            country: place.address_components?.find((c) =>
                                c.types.includes("country"),
                            )?.long_name,
                        };

                        // set values on the form
                        for (const [k, v] of Object.entries(updates)) {
                            // @ts-ignore
                            form.setValue(k, v, { shouldDirty: true });
                        }

                        // update the Google place state
                        setGooglePlace(place);
                    }
                },
            );
        }
    };

    return (
        <div className="flex flex-row items-center">
            <If condition={panelMode === "default" && !form.watch("isVirtual")}>
                <div className="space-y-2">
                    <div className="flex flex-row items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-v3-N-500 p-3">
                            <RiMapPinLine size={20} />
                        </div>
                        <EventLocationName
                            event={event}
                            permissions={permissions}
                        />
                    </div>

                    {!isLoadingEvent && <ValidVenueAlert event={event} />}
                </div>
            </If>

            <If condition={panelMode === "edit"}>
                <div className="w-full ">
                    <div className="flex w-full flex-row">
                        <Label className="mb-1 grow text-left">Location</Label>

                        <If condition={isAdmin || isManager}>
                            <LockedSwitch
                                tooltip="Set location recurrences with this same location"
                                hasValue={!!form.watch("locationLocked")}
                                onCheckedChange={setIsLockedVisible}
                            />
                        </If>
                    </div>

                    <div className="flex w-full items-center gap-2">
                        <If condition={!isLockedVisible}>
                            <GoogleMapsAutoComplete
                                label={false}
                                isEditing={true}
                                value={googlePlace}
                                setValue={(val) =>
                                    onUpdateGoogleMaps(val, false)
                                }
                                required={
                                    form.watch("isVirtual")
                                        ? false
                                        : event.eventJourneyStatus ===
                                          EventJourneyStatus.DRAFT_PROPOSED
                                }
                            />
                        </If>
                        <If condition={isLockedVisible}>
                            <GoogleMapsAutoComplete
                                label={false}
                                isEditing={true}
                                value={googlePlaceLocked}
                                setValue={(val) =>
                                    onUpdateGoogleMaps(val, true)
                                }
                            />
                        </If>
                        <Button
                            variant="destructive"
                            className="rounded"
                            onClick={handleRemoveLocation}
                            disabled={
                                !form.watch("googleMapsPlaceId") &&
                                !form.watch("locationLocked")
                            }
                        >
                            <MdOutlineClear />
                        </Button>
                    </div>
                    <VenueRecommender
                        event={event}
                        onSetLocation={setGooglePlaceId}
                    />
                    {/*
                    <If
                        condition={
                            form.getValues("recommendedVenuePlaceId") !==
                            googlePlaceId
                        }
                    >
                        <div>
                            <Button
                                variant="ghost"
                                className="cursor-pointer underline text-v3-primary"
                                onClick={handleUseRecommendedLocation}
                            >
                                Use Recommended Location
                            </Button>
                        </div>
                    </If>
                    */}
                </div>
            </If>
        </div>
    );
};

const LocationHiddenInfo = ({
    isLocationProtected,
    hasLocationAccess,
    isProtectedVisible,
    setIsProtectedVisible,
    event,
    isAttending,
    permissions,
}: {
    isLocationProtected: boolean;
    hasLocationAccess: boolean;
    isProtectedVisible: boolean;
    setIsProtectedVisible: (visible: boolean) => void;
    event: {
        isPreapproved: boolean;
        isMembersOnly: boolean;
        isMembersOnlyTagGated: boolean;
        protectedTiemout: number | null;
        hosts?: { id: string }[] | null;
        attendees?: { status: AttendanceStatus; user: { id: string } }[] | null;
        isVirtual: boolean | null;
        protectedFields: PrismaJson.EventProtectedFields | null;
        currentUserIsHost: boolean | undefined;
    };
    isAttending: boolean;
    permissions?: RouterOutputs["permissions"]["getUserPermissions"];
}) => {
    if (isLocationProtected && hasLocationAccess) {
        return (
            <div className="mt-2 flex flex-row gap-2 rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                <ProtectionTooltip isProtectedVisible={isProtectedVisible} />
                <div className="inline-block whitespace-nowrap">
                    (
                    <Button
                        variant="primaryLink"
                        className="w-auto bg-transparent px-1 shadow-none"
                        onClick={() =>
                            setIsProtectedVisible(!isProtectedVisible)
                        }
                    >
                        <If condition={isProtectedVisible}>hide</If>
                        <If condition={!isProtectedVisible}>show</If>
                    </Button>
                    )
                </div>
            </div>
        );
    }

    if (isLocationProtected && !hasLocationAccess) {
        if (
            !event.isPreapproved &&
            !event.isMembersOnly &&
            !event.isMembersOnlyTagGated &&
            event.hosts?.length === 0
        ) {
            return (
                <div className="mt-2 flex flex-col rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                    This event does not yet have a host, an exact location has
                    not been set.
                </div>
            );
        }

        if (!!event.protectedTiemout && isAttending) {
            return (
                <div className="mt-2 flex flex-col rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                    The location will be disclosed {event.protectedTiemout}h
                    before the event.
                </div>
            );
        }

        if (event.isPreapproved && !isAttending) {
            return (
                <div className="mt-2 flex flex-col rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                    You have to be approved by the host to see the exact
                    location.
                </div>
            );
        }

        if (
            (event.isMembersOnly || event.isMembersOnlyTagGated) &&
            !permissions?.isMember
        ) {
            return (
                <div className="mt-2 flex w-full flex-col rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                    You have to be a member to see the location.
                </div>
            );
        }

        if (
            !event.isPreapproved &&
            !event.isMembersOnly &&
            !event.isMembersOnlyTagGated &&
            event.hosts?.length !== 0 &&
            event.isVirtual === false
        ) {
            return (
                <div className="mt-2 flex flex-col rounded border-l-8 border-yellow-200 bg-yellow-50 bg-opacity-10 p-2 font-semibold md:max-w-none">
                    Host hasn't selected a venue yet
                </div>
            );
        }
    }

    return null;
};

export const EventLocationName = ({
    event,
    permissions,
}: {
    event?: {
        googleMapsLink: string | null;
        googleMapsPlaceId: string | null;
        city: string | null;
        googleMapsPlaceName: string | null;
        location: string | null;
        isPreapproved: boolean;
        isMembersOnly: boolean;
        isMembersOnlyTagGated: boolean;
        protectedTiemout: number | null;
        hosts?: { id: string }[] | null;
        attendees?: { status: AttendanceStatus; user: { id: string } }[] | null;
        isVirtual: boolean | null;
        protectedFields: PrismaJson.EventProtectedFields | null;
        currentUserIsHost: boolean | undefined;
    };
    permissions?: RouterOutputs["permissions"]["getUserPermissions"];
}) => {
    if (!event) return null;

    const { data: session } = useSession();
    const [isProtectedVisible, setIsProtectedVisible] = useState(false);

    const getGoogleMapsLink = () => {
        if (event.googleMapsLink) {
            return event.googleMapsLink;
        } else {
            const baseUrl = "https://www.google.com/maps/search/?";
            const queryParams = new URLSearchParams({
                api: "1",
                query: event.city || "Google",
                query_place_id: event.googleMapsPlaceId || "",
            });
            return `${baseUrl}${queryParams}`;
        }
    };

    const [googleMapsLink, setGoogleMapsLink] =
        useState<string>(getGoogleMapsLink());

    useEffect(() => {
        if (event) {
            setGoogleMapsLink(getGoogleMapsLink());
        }
    }, [event]);

    const hasLocationAccess =
        permissions?.isAdmin ||
        permissions?.isManager ||
        !!event.currentUserIsHost;

    const isLocationProtected =
        (!!event.protectedFields?.find((e) => e.name === "location") ||
            event.isPreapproved) &&
        (hasLocationAccess || !event.googleMapsPlaceName);
    const isMapsLinkProtected =
        (!!event.protectedFields?.find((e) => e.name === "map") ||
            event.isPreapproved) &&
        (hasLocationAccess || !event.googleMapsPlaceId);

    const isAttending =
        event.attendees?.some(
            (a) =>
                a.status === AttendanceStatus.ATTENDING &&
                a.user.id === session?.user.id,
        ) ?? true;

    return (
        <div className="flex w-full flex-row gap-3">
            <div className="flex flex-row items-center">
                <If condition={!isMapsLinkProtected || isProtectedVisible}>
                    <Link
                        href={googleMapsLink}
                        className={cn(
                            isMapsLinkProtected && "hover:text-primaryLight",
                            "group max-w-[300px] break-words text-left no-underline md:max-w-[460px]",
                        )}
                        target="_blank"
                        type="button"
                    >
                        <p className="flex flex-col">
                            <If
                                condition={
                                    !isProtectedVisible && isLocationProtected
                                }
                            >
                                {event.city}
                            </If>
                            <If
                                condition={
                                    isProtectedVisible || !isLocationProtected
                                }
                            >
                                <span>{event.googleMapsPlaceName}</span>
                                <span
                                    className={`
                                        text-xs
                                        text-v3-N-300
                                        group-hover:text-foreground
                                    `}
                                >
                                    {event.location}
                                </span>
                            </If>
                        </p>
                    </Link>
                </If>
                <If
                    condition={
                        !isProtectedVisible &&
                        isLocationProtected &&
                        isMapsLinkProtected
                    }
                >
                    <p className="flex flex-col">{event.city}</p>
                </If>
                <If
                    condition={
                        !isProtectedVisible &&
                        !isLocationProtected &&
                        isMapsLinkProtected
                    }
                >
                    <p className="flex flex-col">{event.location}</p>
                </If>
            </div>

            <LocationHiddenInfo
                isLocationProtected={isLocationProtected}
                hasLocationAccess={hasLocationAccess}
                isProtectedVisible={isProtectedVisible}
                setIsProtectedVisible={setIsProtectedVisible}
                event={event}
                isAttending={isAttending}
                permissions={permissions}
            />
        </div>
    );
};

export default EventLocation;
